import React from "react";
import { GridApi } from "ag-grid-community";
import { connect } from "react-redux";
import { $, merge } from "glamor";
import { theme } from "styles/theme";
import { DimensionTab } from "./dimension-types";
import { DimensionsToolPanelFooter } from "./dimensions-tool-panel-footer";
import { DimensionToolPanelHeaderContainer } from "./dimensions-tool-panel-header";
import {
    DimensionPanelStateSelectionKind,
    DimensionsPanelReduxState,
} from "reducers/dimensions-panel/dimensions-panel";
import { setShowDoors } from "actions/tool-panel/draft/actions";
import { SetShowDoors } from "actions/tool-panel/draft/types";
import { TabsContainer } from "./tabs";
import { DimensionValuesSelectorContainer } from "./dimension-values-selector/";
import { IStore } from "reducers";
import { createContext } from "hooks/create-context";
import { useErrorMessage } from "./useErrorMessage";
import { useThresholdWarningMessage } from "./useThresholdWarningMessage";
import { AllocationBag } from "helpers/allocation-dimension";
import { getMetricsFromSchema } from "reducers/selectors/collection";

export type CollectionRefs = { [collectionId: string]: string };
interface IProps {
    api: GridApi;
    editable: boolean;
    onImportAttributesToggle: () => void;
    onEditDoorGroupingRequest: () => void;
    isRollupMode: boolean;
    isServerRollup: boolean;
    metricsEnabled: boolean;
    allocationBagRef?: {
        allocationBag: AllocationBag;
    };
}

type ReduxProps = {
    setShowDoors: SetShowDoors;
    draft: DimensionsPanelReduxState;
};

const HEADER_CONTAINER = 95;
const HEADER_SWITCH_CONTAINER = 38;
export const TAB_HEADER_CONTAINER = 48;
const FOOTER_CONTAINER = 50;

type DimensionPanelContext = {
    /**
     * when called opens the modal to edit allocation dimensions
     */
    onEditDoorGroupingRequest: () => void;
    /**
     * this is parsed very high up in the tree from the URLParams
     */
    isRollupMode: boolean;
    editable: boolean;
};

// this context is needed only for the values we cannot derive from redux
export const [useDimensionPanelContext, DimensionPanelContext] = createContext<DimensionPanelContext>();

const DimensionsToolPanelComponent: React.FunctionComponent<IProps & ReduxProps> = ({
    api,
    editable,
    onEditDoorGroupingRequest,
    onImportAttributesToggle,
    isRollupMode,
    isServerRollup,
    draft,
    allocationBagRef,
    metricsEnabled,
}) => {
    const [selectedDimension, setSelectedDimension] = React.useState<SelectedDimension | undefined>();

    const [activeTab, setActiveTab] = React.useState<DimensionTab>(DimensionTab.Doors);

    const handleBack = React.useCallback(() => {
        setSelectedDimension(undefined);
    }, []);

    const handleClosePanel = () => {
        api.closeToolPanel();
    };
    // we need to memoize this to avoid triggering re-rendering of the customers when any of the this component gets re-rendered
    const contextValue = React.useMemo(
        () => ({ isRollupMode, onEditDoorGroupingRequest, editable }),
        [onEditDoorGroupingRequest, isRollupMode, editable]
    );

    const { message, errorHeight } = useErrorMessage(draft, isServerRollup);

    const warningMessage = useThresholdWarningMessage(draft, message);

    const styles = useStyles(Boolean(selectedDimension), errorHeight || 0, Boolean(warningMessage));
    return (
        <DimensionPanelContext value={contextValue}>
            <div {...styles}>
                <div className="dimensionsContainer">
                    <DimensionToolPanelHeaderContainer
                        selectedDimension={selectedDimension}
                        onBack={handleBack}
                        onClosePanel={handleClosePanel}
                    />
                    <div className="dimensionsBodyContainer">
                        {selectedDimension ? (
                            <DimensionValuesSelectorContainer selectedDimension={selectedDimension} />
                        ) : (
                            <TabsContainer
                                setSelectedDimension={setSelectedDimension}
                                setActiveTab={setActiveTab}
                                activeTab={activeTab}
                                onImportAttributesToggle={onImportAttributesToggle}
                                assortmentDimensionMetrics={
                                    metricsEnabled && (isRollupMode || allocationBagRef?.allocationBag !== undefined)
                                }
                            />
                        )}
                    </div>
                    <DimensionsToolPanelFooter
                        warningMessage={warningMessage}
                        isApplyDisabled={Boolean(message)}
                        errorMessage={message}
                        isRollupMode={isRollupMode}
                    />
                </div>
            </div>
        </DimensionPanelContext>
    );
};

export const DimensionsToolPanel = connect<{}, {}, IProps>(
    (state: IStore) => ({
        draft: state.dimensionsPanel.draft,
        metricsEnabled: getMetricsFromSchema(state, state.collection.collectionId).length > 0,
    }),
    {
        setShowDoors: setShowDoors,
    }
)(DimensionsToolPanelComponent);

const useStyles = (isDimensionSelected: boolean, errorHeight: number, hasWarningMessage: boolean) =>
    React.useMemo(() => {
        const footerHeight = FOOTER_CONTAINER + errorHeight + (hasWarningMessage ? 54 : 0);
        return merge(
            {
                backgroundColor: theme.palette.white,
                width: "100%",
                height: "100%",
            },
            $(" .dimensionsContainer", {
                boxShadow: "0 2px 2px 0 rgba(0,0,0,.14), 0 3px 1px -2px rgba(0,0,0,.2), 0 1px 5px 0 rgba(0,0,0,.12)",
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                background: theme.palette.timelineBackgroundColor,
                height: "100%",
                overflowY: "hidden",
            }),
            $(" .dimensionsHeader", {
                lineHeight: "57px",
                height: "57px",
                overflow: "hidden",
                fontSize: "14px",
                padding: "0 16px",
                margin: "0",
                color: theme.palette.darkerGrey,
                fontWeight: 700,
                zIndex: 1,
            }),
            $(" .dimensionsHeaderContainer", {
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: theme.palette.white,
                borderBottom: `1px solid ${theme.palette.borderColor}`,
                borderTop: `1px solid ${theme.palette.borderColor}`,
                zIndex: 1,
                flexDirection: "column",
                boxSizing: "border-box",
                height: `${isDimensionSelected ? HEADER_CONTAINER - HEADER_SWITCH_CONTAINER : HEADER_CONTAINER}px`,
            }),
            $(" .dimensionsBodyContainer", {
                flex: 1,
                height: `calc(100% - ${HEADER_CONTAINER + footerHeight}px)`,
                overflowY: "auto",
            }),
            $(" .dimensionsFooter", {
                lineHeight: "57px",
                height: "57px",
                overflow: "hidden",
                fontSize: "14px",
                padding: "0 16px",
                margin: "0",
                color: theme.palette.darkerGrey,
                flexGrow: 0,
                flexShrink: 0,
                fontWeight: 700,
                zIndex: 1,
            }),
            $(" .dimensionsFooterContainer", {
                display: "flex",
                flexDirection: "column",
                zIndex: 1,
                height: `${footerHeight}px`,
                backgroundColor: theme.palette.white,
                borderTop: `1px solid ${theme.palette.borderColor}`,
                boxSizing: "border-box",
            }),
            $(" .dimensionsFooterButtonContainer", {
                boxSizing: "border-box",
            }),
            $(" .dimensionsErrorContainer", {
                display: "flex",
                flexDirection: "row",
                width: "100%",
                padding: "1rem",
                backgroundColor: `${theme.palette.errorAlertBackground} !important`,
            }),
            $(" .dimensionsWarningContainer", {
                display: "flex",
                flexDirection: "row",
                width: "100%",
                padding: "1rem",
                backgroundColor: `${theme.palette.warningAlertBackground} !important`,
            }),
            $(" span.errorText", {
                fontFamily: "Lato",
                fontSize: "1.2rem",
                color: `${theme.palette.errorAlertTextColor} !important`,
            }),
            $(" span.warningText", {
                fontFamily: "Lato",
                fontSize: "1.2rem",
                color: `${theme.palette.warningAlertTextColor} !important`,
            }),
            $(" .dimensionsCloseButton svg", {
                fontSize: "25px !important",
            }),
            $(" .dimensionsBackButton svg", {
                fontSize: "25px !important",
            }),
            $(" .tab", {
                backgroundColor: `${theme.palette.disabledButtonColor} !important`,
                color: `${theme.palette.darkGrey} !important`,
                padding: "0 30px !important",
                textTransform: "none !important",
            }),
            $(" .tabpanel", {
                height: `calc(100% - ${TAB_HEADER_CONTAINER}px)`,
                overflowX: "hidden",
            }),
            $(" .tabWrapper", {
                height: "100%",
            }),
            $(" .dimensionsButton", {
                alignItems: "center",
                width: "calc(100% - 10px)",
                margin: "5px !important",
            }),
            $(" .dimensionTitle", {
                display: "flex",
                justifyContent: "space-between",
            }),
            $(" div.switchSection", {
                display: "flex",
            }),
            $(" div.switchSection .Mui-checked + .MuiSwitch-track", {
                backgroundColor: theme.palette.primary1Color,
            }),
            $(" div.switchSection .MuiSwitch-thumb", {
                color: theme.palette.primary1Color,
            }),
            $(" div.switchSection .show-doors-label", {
                lineHeight: "36px",
            }),
            $(" div.hide", {
                display: "none",
            }),
            $(" .dimensionBack", {
                display: "flex",
                alignItems: "center",
            }),
            $(" .dimensionsHeaderName", {
                lineHeight: "57px",
                height: "57px",
                overflow: "hidden",
                fontSize: "14px",
                padding: "0 9px",
                margin: "0",
                color: theme.palette.darkerGrey,
                fontWeight: 700,
                zIndex: 1,
                width: "auto !important",
                maxWidth: "200px",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
            }),
            $(" .toolbarContainer", {
                marginTop: "8px",
            }),
            $(" .MuiList-padding", {
                paddingTop: "0 !important",
            })
        );
    }, [isDimensionSelected, errorHeight, hasWarningMessage]);

export type SelectedDimension = {
    kind: Exclude<DimensionPanelStateSelectionKind, "metrics">;
    id: string;
    index: string;
    label: string;
};
